import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Clip } from "../api/types";
import { clipAnchor, timelineClock } from "../lib/timeline";

export function MatchTimeline({ clips, index, duration, time, offset, seekable, atGap, onStart, onCancel, onSeek, onStep, name }: {
  clips: Clip[]; index: number; duration: number; time: number; offset: number;
  seekable: boolean;
  atGap: boolean;
  onStart: () => void; onCancel: () => void;
  onSeek: (ms: number, near: number | null) => void; onStep: (index: number) => void;
  name: (clip: Clip, role: "batsman" | "bowler") => string;
}) {
  const [mobile, setMobile] = useState(() => matchMedia("(max-width: 599px)").matches);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [inspect, setInspect] = useState(false);
  const [width, setWidth] = useState(1);
  const [announcement, setAnnouncement] = useState("");
  const slider = useRef<HTMLInputElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const held = useRef<ReturnType<typeof setTimeout> | null>(null);
  const active = useRef(false);
  const draft = useRef(0);
  const paintFrame = useRef<number | null>(null);
  const dragBounds = useRef<DOMRect | null>(null);
  const clearPaint = () => {
    if (paintFrame.current != null) cancelAnimationFrame(paintFrame.current);
    paintFrame.current = null;
    dragBounds.current = null;
  };
  const queuePaint = () => {
    if (paintFrame.current != null) return;
    paintFrame.current = requestAnimationFrame(() => {
      paintFrame.current = null;
      if (active.current) setPreview(draft.current);
    });
  };
  const touch = useRef<{ x: number; y: number } | null>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const clearHold = () => { if (held.current) clearTimeout(held.current); held.current = null; };
  const cancel = () => {
    clearHold(); clearPaint();
    if (active.current) onCancel();
    active.current = false;
    setPreview(null); setInspect(false);
  };
  useEffect(() => {
    const q = matchMedia("(max-width: 599px)");
    let wasMobile = q.matches;
    const changed = () => {
      if (q.matches === wasMobile) return;
      wasMobile = q.matches;
      clearHold(); clearPaint();
      if (active.current) { active.current = false; cancelRef.current(); }
      setPreview(null); setInspect(false); setMobile(q.matches); setOpen(false);
    };
    q.addEventListener("change", changed);
    return () => q.removeEventListener("change", changed);
  }, []);
  useEffect(() => {
    const el = slider.current;
    if (!el) return;
    const invalidate = () => { dragBounds.current = null; };
    const ro = new ResizeObserver(() => {
      invalidate(); setWidth(el.getBoundingClientRect().width);
    });
    window.addEventListener("scroll", invalidate, true);
    ro.observe(el);
    return () => { ro.disconnect(); window.removeEventListener("scroll", invalidate, true); };
  }, [mobile, open]);
  useEffect(() => {
    if (!mobile || !open || !dialog.current) return;
    const el = dialog.current;
    const scroll = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    el.showModal();
    return () => {
      clearHold(); clearPaint();
      document.body.style.overflow = scroll;
      el.close();
      if (active.current) { active.current = false; cancelRef.current(); }
      setPreview(null); setInspect(false);
      if (matchMedia("(max-width: 599px)").matches) opener.current?.focus({ preventScroll: true });
      else slider.current?.focus({ preventScroll: true });
    };
  }, [mobile, open]);
  useEffect(() => () => {
    clearHold(); clearPaint();
    if (active.current) cancelRef.current();
  }, []);
  // Source positions only change when the clip list changes, not on pointer movement.
  const ordered = useMemo(() => clips.map((c, k) => ({ ms: clipAnchor(c), k, id: c.id }))
    .sort((a, b) => a.ms - b.ms || a.k - b.k), [clips]);
  const shown = Math.min(duration, Math.max(0, preview ?? time));
  const nearest = nearestIndex(ordered, shown);
  const near = nearest != null && duration > 0 && Math.abs(clipAnchor(clips[nearest]!) - shown) / duration * width <= 8 ? nearest : null;
  const inspected = inspect && near != null ? clips[near]! : null;
  const visited = boundary(ordered, shown, true);
  const stepIndex = preview != null && near != null ? near : index;
  const previous = atGap || (preview != null && near == null) ? ordered[boundary(ordered, shown - .5) - 1]?.k ?? -1 : stepIndex - 1;
  const next = atGap || (preview != null && near == null) ? ordered[boundary(ordered, shown + .5, true)]?.k ?? clips.length : stepIndex + 1;
  const position = duration > 0 ? shown / duration * 100 : 0;
  const move = (clientX: number) => {
    const rect = dragBounds.current ?? (dragBounds.current = slider.current!.getBoundingClientRect());
    if (rect.width <= 0) return;
    // Keep the latest pointer position synchronously, including pointerup before a paint.
    draft.current = Math.max(0, Math.min(duration, (clientX - rect.left) / rect.width * duration));
    queuePaint();
  };
  const start = () => {
    if (!active.current) { active.current = true; onStart(); }
  };
  const commit = (snap: boolean) => {
    clearHold(); clearPaint();
    const k = nearestIndex(ordered, draft.current);
    const close = snap && k != null && duration > 0 && Math.abs(clipAnchor(clips[k]!) - draft.current) / duration * width <= 8 ? k : null;
    onSeek(close == null ? draft.current : clipAnchor(clips[close]!), close);
    setAnnouncement(`${timelineClock(offset + (close == null ? draft.current : clipAnchor(clips[close]!)))}${close == null ? ", match time" : `, clip ${close + 1}`}`);
    active.current = false; setPreview(null); setInspect(false);
  };
  const content = <>
    <span className="timeline-grip" aria-hidden="true" />
    <div className="timeline-head">
      <div><h2 id="match-timeline-title">Match timeline</h2><p>{clips.length} clips <span>·</span> {Math.ceil(duration / 60000)} min</p></div>
      {mobile && <button className="timeline-close" aria-label="Close timeline" onClick={() => { cancel(); setOpen(false); }}>×</button>}
    </div>
    <div className="timeline-selection">
      <button aria-label="Previous clip" data-testid="previous-clip" disabled={previous < 0} onClick={() => { cancel(); onStep(previous); }}><Chevron left /></button>
      <span data-testid="timeline-time">{timelineClock(offset + shown)}<small>{near != null ? `Clip ${near + 1}` : "Match time"}</small></span>
      <button aria-label="Next clip" data-testid="next-clip" disabled={next >= clips.length} onClick={() => { cancel(); onStep(next); }}><Chevron /></button>
    </div>
    <div className={`timeline-space ${preview != null ? "dragging" : ""}`} style={{ "--timeline-position": `${position}%` } as React.CSSProperties}>
      {inspected && <>
        <div className="timeline-stem above" /><div className="timeline-stem below" />
        {(["batsman", "bowler"] as const).map((role) => <div key={role} className={`timeline-preview ${role}`} data-testid={`timeline-${role}`}>
          <span className="timeline-crop" key={`${inspected.id}-${role}-${inspected.crops?.[role]}`}><span aria-hidden="true">{role === "batsman" ? "Ba" : "Bo"}</span>{inspected.crops?.[role] && <img src={inspected.crops[role]!} alt={`${role} crop`} draggable={false} onError={(e) => { e.currentTarget.hidden = true; }} />}</span>
          <span><small>{role.toUpperCase()}</small><strong title={name(inspected, role)}>{name(inspected, role)}</strong></span>
        </div>)}
      </>}
      <div className="timeline-track" aria-hidden="true">
        <div className="timeline-base" /><div className="timeline-fill" />
        <TimelineDots ordered={ordered} duration={duration} visited={visited} near={near} />
        <span className="timeline-playhead"><i /></span>
      </div>
      <input ref={slider} type="range" min={0} max={duration || 1} step="any" value={shown} disabled={!seekable || duration <= 0} aria-label="Match time" aria-valuetext={`${timelineClock(offset + shown)}${near != null ? `, clip ${near + 1}` : ", between clips"}`} data-testid="match-slider"
        onChange={() => { /* Pointer and keyboard commits use the same source clock. */ }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          // This range uses source-time pointer mapping. Cancel native range dragging so
          // its input events cannot restore an older controlled value between paints.
          e.preventDefault(); e.currentTarget.focus({ preventScroll: true });
          dragBounds.current = e.currentTarget.getBoundingClientRect();
          start(); setInspect(false); move(e.clientX); e.currentTarget.setPointerCapture(e.pointerId);
          clearHold(); held.current = setTimeout(() => setInspect(true), 250);
        }}
        onPointerMove={(e) => { if (active.current && e.currentTarget.hasPointerCapture(e.pointerId)) move(e.clientX); }}
        onPointerUp={(e) => { if (!active.current) return; move(e.clientX); commit(true); e.currentTarget.releasePointerCapture(e.pointerId); }}
        onPointerCancel={cancel}
        onLostPointerCapture={() => { if (active.current) cancel(); }}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); cancel(); if (mobile) setOpen(false); return; }
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setInspect((v) => !v); return; }
          const delta = e.key === "ArrowRight" || e.key === "ArrowUp" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowDown" ? -1 : 0;
          if (!delta && e.key !== "Home" && e.key !== "End") return;
          e.preventDefault(); e.stopPropagation();
          const base = active.current ? draft.current : time;
          start(); draft.current = e.key === "Home" ? 0 : e.key === "End" ? duration : Math.max(0, Math.min(duration, base + delta * (e.shiftKey ? 30000 : 5000)));
          queuePaint(); setInspect(true);
        }}
        onKeyUp={(e) => { if (active.current && ["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) { e.preventDefault(); commit(false); } }}
        onBlur={() => { if (active.current) cancel(); }} />
      <div className="timeline-ticks" aria-hidden="true">{[0, .25, .5, .75, 1].map((f) => <span key={f}>{timelineClock(offset + duration * f)}</span>)}</div>
      {!inspected && <p className="timeline-rest">{inspect ? "No clip here — keep sliding" : "Hold the playhead and slide to explore"}</p>}
    </div>
    <p className="timeline-instruction">Each dot is a clip. Hold near one to see its players.</p>
    <span className="sr-only" role="status">{announcement}</span>
  </>;
  return <>
    {mobile && <button ref={opener} className="timeline-reveal" aria-haspopup="dialog" aria-expanded={open} data-testid="open-timeline" onClick={() => setOpen(true)}
      onTouchStart={(e) => { const t = e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null; }}
      onTouchEnd={(e) => { const t = e.changedTouches[0]; if (touch.current && t && touch.current.y - t.clientY > 30 && Math.abs(touch.current.x - t.clientX) < 60) setOpen(true); touch.current = null; }}>
      <span className="timeline-grip" />Swipe up to see timeline<Chevron up />
    </button>}
    {mobile ? createPortal(<dialog ref={dialog} className="match-timeline" aria-labelledby="match-timeline-title" onCancel={(e) => { e.preventDefault(); cancel(); setOpen(false); }}
      onKeyDown={(e) => {
        if (e.key !== "Tab") return;
        const nodes = [...e.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),[tabindex="0"]')].filter((el) => el.getClientRects().length);
        const target = e.shiftKey ? nodes.at(-1) : nodes[0];
        if (document.activeElement === (e.shiftKey ? nodes[0] : nodes.at(-1))) { e.preventDefault(); target?.focus(); }
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.getBoundingClientRect(); if (e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom) { cancel(); setOpen(false); } } }}>{open && content}</dialog>, document.body)
      : <section className="match-timeline" aria-labelledby="match-timeline-title">{content}</section>}
  </>;
}

function Chevron({ left = false, up = false }: { left?: boolean; up?: boolean }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d={up ? "m6 15 6-6 6 6" : left ? "m14 6-6 6 6 6" : "m10 6 6 6-6 6"} /></svg>;
}


type TimelinePoint = { ms: number; k: number; id: string };

/** First point at/after time, or strictly after it when inclusive is true. */
function boundary(points: TimelinePoint[], ms: number, inclusive = false): number {
  let lo = 0, hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (points[mid]!.ms < ms || (inclusive && points[mid]!.ms === ms)) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function nearestIndex(points: TimelinePoint[], ms: number): number | null {
  if (!points.length) return null;
  const right = boundary(points, ms), left = right - 1;
  if (left < 0) return points[0]!.k;
  if (right < points.length && points[right]!.ms - ms < ms - points[left]!.ms) return points[right]!.k;
  // Resolve equal timestamps by original list order, including from their right side.
  return points[boundary(points, points[left]!.ms)]!.k;
}

// Dragging between markers only moves the playhead/fill/crops. The dot tree is
// reconciled when a marker's visited/near status changes, not every pointer frame.
const TimelineDots = memo(function TimelineDots({ ordered, duration, visited, near }: {
  ordered: TimelinePoint[]; duration: number; visited: number; near: number | null;
}) {
  return <>{ordered.map((p, order) => <i key={p.id}
    className={`timeline-dot ${order < visited ? "visited" : ""} ${near === p.k ? "near" : ""}`}
    style={{ left: `${Math.min(100, duration > 0 ? p.ms / duration * 100 : 0)}%` }}
    data-testid="timeline-dot" data-index={p.k} />)}</>;
});
